import { Badge, Skeleton } from '@common/components';
import { cn } from '@core/utils';

import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { NewsCardFooter, type NewsCardFooterProps } from '@/components/server';

interface NewsCardProps extends NewsCardFooterProps {
  dday?: string;
  agencyLogo?: string;
  policyName?: string;
  agency?: string;
  dateText?: string;
  supportBenefit?: string;
  href?: string;
  border?: boolean;
}

export const RecommendNewsCard = ({
  dday,
  border = false,
  agencyLogo,
  policyName,
  agency,
  dateText,
  supportBenefit,
  href = '#',
  isLoading,

  ...restProps
}: NewsCardProps) => {
  return (
    <article
      className={cn(
        'flex flex-col gap-16 rounded-small bg-Gray-white p-16',
        border && 'border border-Gray-300',
      )}
    >
      {isLoading ? (
        <div className="flex cursor-pointer flex-col gap-8 text-inherit no-underline">
          <Skeleton className="h-18 w-50" />
          <div className="flex items-center gap-8">
            <Skeleton className="size-48 shrink-0 rounded-small border border-Gray-300" />
            <div className="flex h-[3em] w-full flex-col gap-4">
              <Skeleton className="h-[1em] w-full" />
              <Skeleton className="h-[1em] w-3/5" />
            </div>
          </div>
          <div className="font-normal flex items-center gap-8 text-[0.75rem] text-Gray-700">
            <Skeleton className="h-14 w-90" />
            <Skeleton className="h-14 w-90" />
            <Skeleton className="h-14 w-90" />
          </div>
        </div>
      ) : (
        <>
          <CustomLink className="flex cursor-pointer flex-col gap-8 text-inherit no-underline" href={href}>
            {isLoading ? (
              <Skeleton className="h-18 w-50" />
            ) : (
              <Badge className="bg-R-50 text-R-500">{dday}</Badge>
            )}
            <div className="flex items-center gap-8">
              <div className={cn('relative size-48 min-h-48 min-w-48 rounded-xsmall border border-Gray-400 [&>img]:rounded-xsmall')}>
                <Image
                  alt={`policy-card-${policyName}`}
                  className="object-cover"
                  fill
                  sizes="48px"
                  src={agencyLogo}
                />
              </div>
              <h3 className="font-semibold leading-24 m-0 line-clamp-2 overflow-hidden text-ellipsis whitespace-normal break-keep text-[1rem]">{policyName}</h3>
            </div>
            <div className="font-normal flex items-center gap-8 text-[0.75rem] text-Gray-700 [&>span:not(:last-child)]:after:pl-8 [&>span:not(:last-child)]:after:text-[0.75rem] [&>span:not(:last-child)]:after:text-Gray-400 [&>span:not(:last-child)]:after:content-['|']">
              <span className="block max-w-full truncate">{agency}</span>
              {dateText ? (
                <span className="block max-w-full truncate">{dateText}</span>
              ) : null}
              {supportBenefit ? (
                <span className="block max-w-full truncate">
                  {supportBenefit}
                </span>
              ) : null}
            </div>
          </CustomLink>
        </>
      )}
      <NewsCardFooter {...restProps} isLoading={isLoading} />
    </article>
  );
};
